import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, type ReactNode } from 'react'
import { api, ApiError } from '../api/client'
import type { User } from '../api/types'

interface AuthState {
	user: User | null
	loading: boolean
	signIn(email: string, password: string): Promise<void>
	signOut(): Promise<void>
	setUser(user: User): void
}

const AuthContext = createContext<AuthState | null>(null)

async function fetchMe(): Promise<User | null> {
	try {
		return await api.get<User>('/api/auth/me')
	} catch (error) {
		if (error instanceof ApiError && error.status === 401) return null
		throw error
	}
}

export function AuthProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient()
	const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 5 * 60_000 })
	const setUser = (user: User | null) => queryClient.setQueryData(['me'], user)
	const state: AuthState = {
		user: me.data ?? null,
		loading: me.isPending,
		async signIn(email, password) {
			setUser(await api.post<User>('/api/auth/login', { email, password }))
		},
		async signOut() {
			await api.post('/api/auth/logout')
			queryClient.clear()
			setUser(null)
		},
		setUser,
	}
	return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
	const ctx = useContext(AuthContext)
	if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
	return ctx
}
